/**
 * The agents list.
 *
 * IT IS A TABLE OF CONFIGURATION, NOT OF PERFORMANCE, and that is why it is
 * short. What a reader wants here is which model an agent is on, whether it is
 * the cheap tier, and how many tools it may call — the three fields that change
 * the cost and the capability of every call it takes. Latency belongs to a
 * session, not to an agent, so none of it is repeated here; the sessions list
 * filters by agent for that.
 *
 * "NEW AGENT" CREATES FIRST AND ASKS QUESTIONS LATER: one click POSTs a row
 * with a starter prompt and server defaults for everything else, then lands on
 * the editor. Same pattern as the tools page — a form here would duplicate the
 * editor this app already has, one navigation away.
 */
import { useEffect, useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { ApiError, agents, type AgentConfig } from '@/api'
import { Button } from '@/components/ui/button'
import { navigate } from '@/components/app-sidebar'
import { DataTable, PageHeader, type Column } from '@/components/data-table'
import { fmtInt, fmtWhen } from '@/components/state-pill'

/** Enough prompt to take a test call the moment the row exists; every word is
 *  expected to be replaced in the editor this button lands on. */
const STARTER_INSTRUCTIONS =
  'You are a helpful voice assistant. Keep your answers short and conversational — '
  + 'one or two sentences unless the caller asks for more.'

export function AgentsPage({
  onRows,
}: {
  /**
   * Hands the loaded rows up to the shell, which needs them for the nav badge
   * and for the agent id behind "Start a call". Reported rather than fetched
   * twice: the shell already has a copy from mount, and this keeps it fresh
   * whenever somebody looks at this screen.
   */
  onRows?: (rows: AgentConfig[]) => void
}) {
  const [rows, setRows] = useState<AgentConfig[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const createAgent = async () => {
    if (creating) return
    setCreating(true)
    try {
      const created = await agents.create(
        { name: 'New agent', instructions: STARTER_INSTRUCTIONS },
        new AbortController().signal,
      )
      navigate(`/agents/${created.id}`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
      setCreating(false)
    }
  }

  useEffect(() => {
    const ac = new AbortController()
    agents
      .list(ac.signal)
      .then((res) => {
        setRows(res.rows)
        setError(null)
        onRows?.(res.rows)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setError(e instanceof ApiError ? e.message : String(e))
      })
    return () => ac.abort()
    // onRows is a parent callback, recreated on every parent render. Depending
    // on it would refetch the list every time anything in the shell re-rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns: Column<AgentConfig>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (a) => <span className="font-medium">{a.name}</span>,
    },
    {
      key: 'model',
      header: 'Model',
      width: '14rem',
      cell: (a) => (
        <span className="mono">
          {a.model}
          {/* The -mini tier is a ~58% cost delta on audio tokens for one config
              field, so it is worth calling out rather than leaving a reader to
              spot a suffix. */}
          {a.model.endsWith('-mini') && (
            <span className="pill pill-plain ml-1" title="Cheaper tier: $10/$20 per 1M audio in/out against $32/$64.">
              cheap tier
            </span>
          )}
        </span>
      ),
    },
    { key: 'voice', header: 'Voice', width: '7rem', cell: (a) => <span className="mono">{a.voice}</span> },
    {
      key: 'vadMode',
      header: 'VAD',
      width: '8rem',
      cell: (a) => <span className="mono">{a.vadMode}</span>,
    },
    {
      key: 'transcribeModel',
      header: 'Transcription',
      width: '11rem',
      hideBelow: 'lg',
      cell: (a) => <span className="mono">{a.transcribeModel}</span>,
    },
    {
      key: 'tools',
      header: 'Tools',
      numeric: true,
      width: '5rem',
      cell: (a) => fmtInt(a.tools.length),
    },
    {
      key: 'greeting',
      header: 'Greets first',
      width: '7rem',
      hideBelow: 'md',
      cell: (a) =>
        a.greeting ? (
          <span title={a.greeting}>yes</span>
        ) : (
          <span className="faint" title="No greeting: the agent waits for the caller to speak first.">
            no
          </span>
        ),
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      width: '11rem',
      hideBelow: 'xl',
      cell: (a) => <span className="whitespace-nowrap">{fmtWhen(a.updatedAt)}</span>,
    },
  ]

  return (
    <div className="min-w-0">
      <PageHeader
        title="Agents"
        subtitle={
          <>
            What a call is configured with before it starts: the prompt, the voice, the
            model tier, and which registered tools the model is allowed to call.
          </>
        }
        actions={
          <Button className="cursor-pointer" disabled={creating} onClick={() => void createAgent()}>
            <PlusIcon aria-hidden />
            {creating ? 'Creating…' : 'New agent'}
          </Button>
        }
      />

      {error && <p className="err mb-4">{error}</p>}

      <DataTable
        columns={columns}
        rows={rows}
        keyOf={(a) => a.id}
        onRowClick={(a) => navigate(`/agents/${a.id}`)}
        emptyTitle="No agents configured"
        emptyBody={
          <>
            A session cannot be created without an agent. Use{' '}
            <span className="font-medium">New agent</span> above to create the first one —
            it lands you in the editor with working defaults.
          </>
        }
      />
    </div>
  )
}
